import { useEffect, useState } from 'react';
import { Check, ChevronDown, Loader2, PenLine } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { toDateInputValue } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StatusPill, ToneDot } from '@/components/ui/status-pill';
import {
  REVIEW_ORDER, REVIEW_PARTIES, REVIEW_STATUSES, reviewStatusOf, reviewSubLine, reviewSubTitle, submittedAgeDays,
} from './documentsModel';

const todayInput = () => toDateInputValue(new Date());

/** The status pill + "Consultant · 3 Feb" of one version (16 idea 8). */
export function ReviewStatusPill({ doc, size = 'sm', className }) {
  const status = reviewStatusOf(doc);
  const meta = REVIEW_STATUSES[status];
  return <StatusPill tone={meta.tone} label={meta.label} size={size} className={className} data-testid={`doc-status-${doc.id}`} />;
}

function ReviewForm({ doc, onSaved, onClose }) {
  const current = reviewStatusOf(doc);
  const [status, setStatus] = useState(current);
  const [party, setParty] = useState(doc.review_party || 'consultant');
  const [day, setDay] = useState(todayInput());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const needsParty = Boolean(REVIEW_STATUSES[status]?.party);
  const unchanged = status === current && doc.review_tracked && (!needsParty || party === doc.review_party) && !note.trim()
    && day === toDateInputValue(doc.review_date);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await api.put(`/documents/${encodeURIComponent(doc.id)}/review`, {
        status, party: needsParty ? party : null, date: day, note: note.trim() || null,
      });
      onSaved(response.data || {}, { status, party: needsParty ? party : '' });
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the status'));
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`doc-status-form-${doc.id}`}>
      <div>
        <p className="text-[13px] font-bold text-ink">Status of <span className="font-mono">v{doc.version || 1}</span></p>
        <p className="text-[11.5px] text-ink-3">Where this version stands with the consultant or client.</p>
      </div>
      {/* Two columns, one line each (the chosen one's meaning below): the whole form fits a laptop screen. */}
      <div>
        <div role="radiogroup" aria-label="Status" className="grid grid-cols-2 gap-1">
          {REVIEW_ORDER.map((key) => {
            const meta = REVIEW_STATUSES[key];
            const selected = key === status;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-describedby={selected ? `doc-status-hint-${doc.id}` : undefined}
                title={meta.hint}
                onClick={() => setStatus(key)}
                data-testid={`doc-status-option-${key}`}
                className={cn(
                  'flex min-h-[32px] w-full items-center gap-1.5 rounded-lg border px-2 py-1 text-left text-[12.5px] font-semibold leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selected ? 'border-[color:var(--brand-soft-border)] bg-brand-soft text-ink' : 'border-transparent text-ink-2 hover:bg-surface-hover'
                )}
              >
                <ToneDot tone={meta.tone === 'outline' ? 'gray' : meta.tone} size={8} />
                <span className="min-w-0 flex-1">{meta.label}</span>
                {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-brand-ink" aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
        <p id={`doc-status-hint-${doc.id}`} className="mt-1.5 text-[11.5px] text-ink-3" data-testid="doc-status-hint">
          {REVIEW_STATUSES[status]?.hint}
        </p>
      </div>
      {needsParty ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="mb-1 text-[11.5px] font-semibold text-ink-3">{status === 'submitted' ? 'Submitted to' : 'Reply from'}</p>
            <div role="group" aria-label={status === 'submitted' ? 'Submitted to' : 'Reply from'} className="inline-flex w-full rounded-lg bg-[color:var(--status-gray-bg)] p-[3px]">
              {Object.entries(REVIEW_PARTIES).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={party === key}
                  onClick={() => setParty(key)}
                  data-testid={`doc-status-party-${key}`}
                  className={cn(
                    'h-7 flex-1 rounded-md text-[12.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    party === key ? 'bg-surface text-ink shadow-card' : 'text-ink-2'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="mb-1 block text-[11.5px] font-semibold text-ink-3">On</span>
            <input
              type="date"
              value={day}
              max={todayInput()}
              onChange={(event) => setDay(event.target.value)}
              className="h-[34px] w-full rounded-lg border border-line-2 bg-surface px-2 text-[12.5px] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="doc-status-date"
            />
          </label>
        </div>
      ) : null}
      <label className="block">
        <span className="mb-1 block text-[11.5px] font-semibold text-ink-3">Note <span className="font-normal">(optional)</span></span>
        <input
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          placeholder={status === 'revise_resubmit' ? 'e.g. Comments on cable sizes, sheet 3' : 'e.g. Sent with transmittal TR-114'}
          className="h-[34px] w-full rounded-lg border border-line-2 bg-surface px-2 text-[12.5px] text-ink placeholder:text-ink-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="doc-status-note"
        />
      </label>
      {error ? <p className="text-[12px] font-medium text-status-red-ink" role="alert">{error}</p> : null}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="button" size="sm" onClick={save} disabled={saving || unchanged || !day} data-testid="doc-status-save">
          {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
          Save status
        </Button>
      </div>
    </div>
  );
}

/**
 * The Status cell: pill + "Client · 3 d" (16 idea 8). People who may edit click the pill to log a submission or
 * a reply (status, Consultant / Client, date, note); every change is in the version's review trail with Undo.
 */
export default function ReviewStatusControl({ doc, canEdit, onSaved, align = 'start', trigger = 'pill' }) {
  const [open, setOpen] = useState(false);
  const sub = reviewSubLine(doc);
  const subTitle = reviewSubTitle(doc) || sub;
  const age = submittedAgeDays(doc);
  // A submission nobody has answered for a month is worth noticing.
  const subTone = age !== null && age > 30 ? 'text-status-amber-ink font-semibold' : 'text-ink-3';
  useEffect(() => { if (!canEdit) setOpen(false); }, [canEdit]);

  const content = (
    <span className="flex min-w-0 flex-col items-start gap-0.5">
      <span className="relative inline-flex max-w-full items-center">
        <ReviewStatusPill doc={doc} />
        {canEdit ? (
          <ChevronDown className="absolute -right-3.5 h-3 w-3 text-ink-4 opacity-0 transition-opacity group-hover/status:opacity-100 group-focus-visible/status:opacity-100" aria-hidden="true" />
        ) : null}
      </span>
      {sub ? <span className={cn('max-w-full truncate text-[11.5px]', subTone)} title={subTitle} data-testid={`doc-status-sub-${doc.id}`}>{sub}</span> : null}
    </span>
  );

  if (!canEdit) return trigger === 'button' ? null : content;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger === 'button' ? (
          <Button type="button" size="xs" variant="outline" data-testid={`doc-status-update-${doc.id}`}>
            <PenLine aria-hidden="true" />
            {doc.review_tracked ? 'Update status' : 'Log submission or reply'}
          </Button>
        ) : (
        <button
          type="button"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className="group/status -m-1 max-w-[calc(100%+0.5rem)] rounded-md p-1 text-left hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Status of ${doc.title} v${doc.version || 1}: ${REVIEW_STATUSES[reviewStatusOf(doc)].label}${sub ? ` (${sub})` : ''} — change`}
          data-testid={`doc-status-button-${doc.id}`}
          data-row-click="ignore"
        >
          {content}
        </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        collisionPadding={12}
        // Never taller than the room Radix found: on a short screen the form scrolls inside, Save stays reachable.
        className="max-h-[var(--radix-popover-content-available-height)] w-[300px] overflow-y-auto rounded-xl border-line bg-surface p-3.5 shadow-overlay"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {open ? <ReviewForm doc={doc} onSaved={onSaved} onClose={() => setOpen(false)} /> : null}
      </PopoverContent>
    </Popover>
  );
}
